import React from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import { AppleButton } from '@/components/ui/apple-button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@morningai/shared-ui'
import { withTranslation, WithTranslation } from 'react-i18next'

interface ErrorBoundaryProps extends WithTranslation {
  children: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
  errorId: string | null
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { 
      hasError: true,
      errorId: Math.random().toString(36).substr(2, 9)
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({
      error,
      errorInfo
    })

    if (window.Sentry) {
      window.Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack
          }
        },
        tags: {
          section: 'error_boundary'
        }
      })
    }

    console.error('Error Boundary caught an error:', error, errorInfo)
  }

  handleRetry(): void {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    })
  }

  handleGoHome(): void {
    window.location.href = '/dashboard'
  }

  render() {
    const { t } = this.props
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-error-50 to-orange-100 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="w-8 h-8 text-error-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-neutral-900">
                {t('errorBoundary.title')}
              </CardTitle>
              <CardDescription className="text-lg text-neutral-600 mt-2">
                {t('errorBoundary.description')}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              <div className="bg-neutral-50 p-4 rounded-lg">
                <h3 className="font-semibold text-neutral-900 mb-2">{t('errorBoundary.errorDetailsTitle')}</h3>
                <p className="text-sm text-neutral-600 mb-2">
                  {t('errorBoundary.errorIdLabel')} <code className="bg-neutral-200 px-2 py-1 rounded text-xs">{this.state.errorId}</code>
                </p>
                {this.state.error && (
                  <p className="text-sm text-error-600 font-mono bg-error-50 p-2 rounded">
                    {this.state.error.toString()}
                  </p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <AppleButton 
                  onClick={this.handleRetry.bind(this)} 
                  className="flex items-center gap-2"
                  aria-label={t('errorBoundary.reloadAriaLabel')}
                >
                  <RefreshCw className="w-4 h-4" />
                  {t('errorBoundary.reloadButton')}
                </AppleButton>
                <AppleButton 
                  variant="outline" 
                  onClick={this.handleGoHome.bind(this)}
                  className="flex items-center gap-2"
                  aria-label={t('errorBoundary.homeAriaLabel')}
                >
                  <Home className="w-4 h-4" />
                  {t('errorBoundary.homeButton')}
                </AppleButton>
              </div>

              <div className="text-center text-sm text-neutral-600">
                {t('errorBoundary.supportMessage')}
              </div>
            </CardContent>
          </Card>
        </div>
      )
    }

    return this.props.children
  }
}

export default withTranslation()(ErrorBoundary)
